/**
 * SlipRollupCard — portfolio-level view of wave slip across all estimates.
 * Surfaces late programmes early; click a row to open that estimate's waves.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { TrendingDown, ChevronDown, ChevronRight, AlertOctagon } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TREND_COLORS = ['#EF4444', '#F59E0B', '#8B5CF6', '#4ABFCF', '#10B981'];

const STATUS_CLS = {
  planned: 'bg-slate-100 text-slate-600 border-slate-300',
  in_flight: 'bg-sky-100 text-sky-700 border-sky-300',
  launched: 'bg-emerald-100 text-emerald-700 border-emerald-300',
};

function slipBadge(slip) {
  if (slip == null) return null;
  if (slip === 0) return <Badge className="text-[9px] border bg-emerald-50 text-emerald-700 border-emerald-300">On time</Badge>;
  if (slip > 0) return <Badge className="text-[9px] border bg-rose-50 text-rose-700 border-rose-300">{slip}d late</Badge>;
  return <Badge className="text-[9px] border bg-emerald-50 text-emerald-700 border-emerald-300">{Math.abs(slip)}d early</Badge>;
}

export function SlipRollupCard({ onOpenEstimate, currentEstimateId, showTrend = true }) {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [open, setOpen] = useState(true);
  const [thresholdInput, setThresholdInput] = useState('');

  const loadRollup = () => {
    axios.get(`${API_URL}/api/planner/waves-slip-rollup`, { withCredentials: true })
      .then(res => {
        setData(res.data);
        setThresholdInput(String(res.data?.summary?.threshold_days ?? 7));
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadRollup();
    axios.get(`${API_URL}/api/planner/waves-slip-trend?days=90`, { withCredentials: true })
      .then(res => setTrend(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEstimateId]);

  const saveThreshold = () => {
    const v = parseInt(thresholdInput, 10);
    if (!v || v < 1 || v > 365) { toast.error('Threshold must be 1–365 days'); return; }
    if (v === data?.summary?.threshold_days) return;
    axios.put(`${API_URL}/api/planner/slip-threshold`, { threshold_days: v }, { withCredentials: true })
      .then(() => { toast.success(`Slip alert threshold set to ${v} days`); loadRollup(); })
      .catch(err => toast.error(err?.response?.data?.detail || 'Could not save threshold'));
  };

  const trendChart = useMemo(() => {
    if (!trend?.series?.length) return null;
    const top = trend.series.slice(0, 5);
    const byDate = {};
    top.forEach(s => (s.points || []).forEach(p => {
      byDate[p.date] = byDate[p.date] || { date: p.date };
      byDate[p.date][s.estimate_name] = p.worst_slip_days;
    }));
    const rows = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
    if (!rows.length) return null;
    return { rows, names: top.map(s => s.estimate_name) };
  }, [trend]);

  if (!data || !data.estimates?.length) return null;
  const s = data.summary || {};

  return (
    <Card data-testid="slip-rollup-card">
      <CardHeader className="pb-2">
        <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left" data-testid="slip-rollup-toggle">
          <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-[#4ABFCF]" /> Portfolio Wave Slip
            <Badge variant="secondary" className="text-[10px]">{s.total_estimates} programme{s.total_estimates === 1 ? '' : 's'}</Badge>
            {s.over_threshold > 0 && (
              <Badge className="text-[10px] border bg-rose-600 text-white border-rose-700 font-semibold" data-testid="slip-rollup-threshold-badge">
                <AlertOctagon className="w-2.5 h-2.5 mr-1" /> {s.over_threshold} over {s.threshold_days}d threshold
              </Badge>
            )}
            {s.late_waves > 0 ? (
              <Badge className="text-[10px] border bg-rose-50 text-rose-700 border-rose-300" data-testid="slip-rollup-late-badge">
                {s.late_waves} late wave{s.late_waves === 1 ? '' : 's'}
              </Badge>
            ) : (
              <Badge className="text-[10px] border bg-emerald-50 text-emerald-700 border-emerald-300" data-testid="slip-rollup-ontime-badge">
                No slip detected
              </Badge>
            )}
          </CardTitle>
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </button>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {s.worst ? (
            <p className="text-[11px] text-slate-500" data-testid="slip-rollup-worst">
              Worst slip: <strong className="text-rose-600">{s.worst.estimate_name} — {s.worst.wave} ({s.worst.slip_days}d late)</strong>
            </p>
          ) : <span />}
          {isAdmin && open && (
            <div className="flex items-center gap-1.5 text-[10px] text-slate-400" data-testid="slip-threshold-editor">
              Alert threshold
              <Input type="number" min="1" max="365" value={thresholdInput}
                onChange={(e) => setThresholdInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveThreshold()}
                className="h-6 w-16 text-[11px]" data-testid="slip-threshold-input" />
              days
              {parseInt(thresholdInput, 10) !== (data?.summary?.threshold_days ?? 7) && (
                <button type="button" onClick={saveThreshold}
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#0F2040] text-white hover:bg-[#1a3057]"
                  data-testid="slip-threshold-save-btn">
                  Set
                </button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="pt-0 space-y-3">
          <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
            {data.estimates.map((e, i) => (
              <button
                key={e.estimate_id}
                type="button"
                onClick={() => onOpenEstimate?.(e.estimate_id)}
                className={`w-full text-left px-2.5 py-2 rounded border transition-colors hover:border-[#4ABFCF]/60 ${
                  e.over_threshold
                    ? 'border-rose-400 bg-rose-100/70 ring-1 ring-rose-300'
                    : e.worst_slip_days > 0
                      ? 'border-rose-200 bg-rose-50/40'
                      : 'border-slate-100 bg-white'
                } ${e.estimate_id === currentEstimateId ? 'ring-1 ring-[#4ABFCF]/50' : ''}`}
                data-testid={`slip-rollup-row-${i}`}
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-[#0F2040] truncate">{e.estimate_name}</span>
                  {e.organization_name && <span className="text-[10px] text-slate-400">{e.organization_name}</span>}
                  {e.over_threshold && (
                    <Badge className="text-[9px] border bg-rose-600 text-white border-rose-700 font-semibold" data-testid={`slip-over-threshold-badge-${i}`}>
                      <AlertOctagon className="w-2.5 h-2.5 mr-0.5" /> Over threshold
                    </Badge>
                  )}
                  {e.worst_slip_days > 0 && (
                    <Badge className="text-[9px] border bg-rose-50 text-rose-700 border-rose-300 ml-auto">worst {e.worst_slip_days}d</Badge>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap mt-1">
                  {e.waves.map((w, wi) => (
                    <span key={`${w.name}-${wi}`} className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                      <Badge className={`text-[9px] border ${STATUS_CLS[w.status] || STATUS_CLS.planned}`}>{w.name}</Badge>
                      {slipBadge(w.slip_days)}
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
          {showTrend && trendChart && (
            <div className="pt-2 border-t border-slate-100" data-testid="slip-trend-chart">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">
                Slip trend — worst days late per programme (last 90 days)
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={trendChart.rows} margin={{ top: 4, right: 8, bottom: 0, left: -18 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#94a3b8' }}
                    tickFormatter={(d) => (d || '').slice(5)} />
                  <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} allowDecimals={false}
                    label={{ value: 'days late', angle: -90, position: 'insideLeft', offset: 24, style: { fontSize: 9, fill: '#94a3b8' } }} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }}
                    formatter={(v) => [`${v}d late`, undefined]} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {trendChart.names.map((name, i) => (
                    <Line key={name} type="monotone" dataKey={name}
                      stroke={TREND_COLORS[i % TREND_COLORS.length]}
                      strokeWidth={2} dot={{ r: 3 }} connectNulls isAnimationActive={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default SlipRollupCard;
